import { logoutUser } from "../actions/authActions";
import store from "../store";
import axios from "axios";

class IdleTimer {
  constructor() {
    this.idleThreshold = 5 * 60 * 1000; // 5 minutes
    this.events = [
      "mousemove", "mousedown", "keypress", "scroll", "touchstart", "click"
    ];
    this.eventHandler = this.onActivity.bind(this);

    this.idleTimer = null;
    this.countdownInterval = null;
    this.remainingTime = this.idleThreshold;

    this.breakTime = this.getStoredBreakTime();
    this.startTime = this.getStoredLoginTime() || Date.now();
    this.lastActivityTime = this.startTime; // initialize last activity time

    this.activityPercentage = 0; // 🟢 On initial login: activityPercentage = 0
    localStorage.setItem("activityPercentage", this.activityPercentage);

    // 🟢 Listen for activityPercentage changes from other tabs (real-time sync)
    window.addEventListener("storage", (event) => {
      if (event.key === "activityPercentage") {
        const newValue = parseInt(event.newValue, 10) || 0;
        if (this.activityPercentage !== newValue) {
          this.activityPercentage = newValue;
          // Optionally, update UI or trigger any listeners here for real-time effect
        }
      }
    });

    // 🟢 Listen for focus to immediately sync activityPercentage from storage
    window.addEventListener("focus", () => {
      const latest = parseInt(localStorage.getItem("activityPercentage"), 10) || 0;
      if (this.activityPercentage !== latest) {
        this.activityPercentage = latest;
        // Optionally, update UI or trigger any listeners here for real-time effect
      }
    });

    this.start();
    this.activityMonitor();
  }

  start() {
    this.storeLoginTime();
    this.events.forEach(event =>
      window.addEventListener(event, this.eventHandler)
    );
    document.addEventListener("visibilitychange", this.handleVisibility.bind(this));
    this.startCountdown();
  }

  onActivity() {
    this.remainingTime = this.idleThreshold; // reset to 5 minutes
    const now = Date.now();

    // Update last activity time only if at least 1s has passed
    if (!this.lastActivityTime || now - this.lastActivityTime > 1000) {
      this.lastActivityTime = now;

      // 🟢 Gradually increase activityPercentage per interaction
      let activityPercentage = parseInt(localStorage.getItem("activityPercentage"), 10) || 0;
      activityPercentage = Math.min(100, activityPercentage + 10); // Increase by 10, max 100
      this.activityPercentage = activityPercentage;
      localStorage.setItem("activityPercentage", activityPercentage);

      // 🟢 Send immediate update to the server
      axios.post("https://web.mybiocalculus.com:3002/api/users/UpdateVerifierActivity", {
        Verifier_id: localStorage.getItem("verifierid"),
        LoginedTime: localStorage.getItem("loginedDateTime"),
        ActivityLevel: activityPercentage,
      }).catch((err) => {
        // console.log("Immediate activity update error:", err);
      });
    }
  }

  startCountdown() {
    this.clearTimers();

    this.countdownInterval = setInterval(() => {
      this.remainingTime -= 1000;
      this.storeRemainingTime();

      if (this.remainingTime <= 0) {
        this.logout();
      }
    }, 1000);
  }

  clearTimers() {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  storeRemainingTime() {
    const minutesLeft = (this.remainingTime / 1000 / 60).toFixed(2);
    localStorage.setItem("remainingLogoutTime", minutesLeft);
  }

  storeLoginTime() {
    if (!localStorage.getItem("loggedInTime")) {
      localStorage.setItem("loggedInTime", new Date().toISOString());
    }
  }

  getStoredLoginTime() {
    const time = localStorage.getItem("loggedInTime");
    return time ? new Date(time).getTime() : null;
  }

  storeBreakTime() {
    localStorage.setItem("breakTime", this.breakTime);
  }

  getStoredBreakTime() {
    return parseFloat(localStorage.getItem("breakTime")) || 0;
  }

  handleVisibility() {
    if (!document.hidden) {
      this.onActivity();
    }
  }

  async logout() {
    this.clearTimers();

    const verifierId = localStorage.getItem("verifierid");
    const verifierType = localStorage.getItem("verifiertype");

    if (
      verifierId === "5d6df589c3d92110fccfde7c" ||
      verifierType === "7"
    ) {
      // console.log("Verifier type 7 - logout skipped");
      return;
    }

    try {
      await axios.post("https://web.mybiocalculus.com:3002/api/users/updateLogoutTime", {
        Verifier_id: verifierId,
        LoginedTime: localStorage.getItem("loginedDateTime")
      });

      await axios.post("https://web.mybiocalculus.com:3002/api/users/UpdateVerifierActivity", {
        Verifier_id: verifierId,
        LoginedTime: localStorage.getItem("loginedDateTime"),
        ActivityLevel: localStorage.getItem("activityPercentage"),
        BreaksConsumed: this.breakTime
      });
    } catch (err) {
      console.log("Logout API error:", err);
    }

    this.events.forEach(event =>
      window.removeEventListener(event, this.eventHandler)
    );
    document.removeEventListener("visibilitychange", this.handleVisibility);

    localStorage.clear();
    store.dispatch(logoutUser());
  }

  activityMonitor() {
    setInterval(() => {
      this.updateActivityLevel();
    }, 3000);
  }

  updateActivityLevel() {
    // 🟢 Decay activityPercentage over time when idle
    let activityPercentage = parseInt(localStorage.getItem("activityPercentage"), 10) || 0;
    if (activityPercentage > 0) {
      activityPercentage = Math.max(0, activityPercentage - 5); // Decay by 5, min 0
      this.activityPercentage = activityPercentage;
      localStorage.setItem("activityPercentage", activityPercentage);
    }
    // 🟢 Sync with other tabs if changed
    if (this.activityPercentage !== activityPercentage) {
      this.activityPercentage = activityPercentage;
    }
    // console.log("Activity Level:", activityPercentage); // log activity level
    axios.post("https://web.mybiocalculus.com:3002/api/users/UpdateVerifierActivity", {
      Verifier_id: localStorage.getItem("verifierid"),
      LoginedTime: localStorage.getItem("loginedDateTime"),
      ActivityLevel: activityPercentage,
    }).catch(err => {
      console.log("Error updating activity level:", err);
    });
  }

  getActivityPercentage() {
    // Calculate percentage based on time since last activity (Web Work method)
    const now = Date.now();
    if (!this.lastActivityTime) {
      const stored = localStorage.getItem("activityPercentage");
      return stored !== null ? parseInt(stored, 10) : 0;
    }
    const lastActivity = this.lastActivityTime || this.startTime || now;
    const elapsed = now - lastActivity;
    // Web Work method: 100 - Math.floor((elapsed / idleThreshold) * 100)
    const percent = Math.max(0, 100 - Math.floor((elapsed / this.idleThreshold) * 100));
    return percent;
  }
}

export default IdleTimer;
